import { Card, Empty, Tag, Tooltip, Typography } from "antd";
import { CloseCircleFilled } from "@ant-design/icons";
import { red } from "@ant-design/colors";
import {
  AssetIoConfigOutput,
  SiteIoConfiguration,
} from "../../../../services/client";
import { flattenObject } from "./utils/siteIoDiffUtils";
import { PhaseRotationWarning } from "./PhaseRotationWarning";

type SiteIoConfigCardProps = {
  configuration: SiteIoConfiguration;
  undeclaredRotations: string[];
};

const UNSUPPORTED_REGISTERS = "unsupported";

function formatValue(value: unknown): string {
  if (value === undefined) {
    return "—";
  }
  if (value === null) {
    return "null";
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value);
}

function AssetCard({ entry }: { entry: AssetIoConfigOutput }) {
  const config = entry.config as Record<string, unknown>;
  const type = (config?.type as string | undefined) ?? "unknown";
  const rest: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(config ?? {})) {
    if (key !== "type") {
      rest[key] = value;
    }
  }
  const flattened = flattenObject(rest);
  const fields = Object.entries(flattened);
  const hasUnsupportedRegisters = fields.some(
    ([, value]) => value === UNSUPPORTED_REGISTERS,
  );

  return (
    <Card
      size="small"
      className="w-full"
      style={
        hasUnsupportedRegisters
          ? { backgroundColor: red[0], borderColor: red[2] }
          : undefined
      }
    >
      <div className="flex items-center gap-2 mb-2">
        <Typography.Text strong className="font-mono">
          {entry.asset_id}
        </Typography.Text>
        <Tag color="blue">{type}</Tag>
      </div>
      {fields.length === 0 ? (
        <Typography.Text type="secondary" className="text-xs">
          No additional fields.
        </Typography.Text>
      ) : (
        <div className="flex flex-col gap-0.5">
          {fields.map(([field, value]) => (
            <div key={field} className="flex items-start gap-2 text-xs">
              <span className="font-mono text-gray-500 min-w-[180px]">
                {field}:
              </span>
              {value === UNSUPPORTED_REGISTERS ? (
                <Tooltip title="No Modbus register template exists for this hardware yet, so the controller cannot read this asset.">
                  <Typography.Text type="danger" strong className="font-mono">
                    <CloseCircleFilled className="mr-1" />
                    {UNSUPPORTED_REGISTERS}
                  </Typography.Text>
                </Tooltip>
              ) : (
                <span className="font-mono break-all">
                  {formatValue(value)}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export const SiteIoConfigCard: React.FC<SiteIoConfigCardProps> = ({
  configuration,
  undeclaredRotations,
}) => {
  const assetEntries = configuration.asset_io_config ?? [];
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Typography.Text type="secondary">Controller mode:</Typography.Text>
        <Tag
          color={
            configuration.controller_mode === "active" ? "green" : "default"
          }
        >
          {configuration.controller_mode ?? "read_only"}
        </Tag>
      </div>
      <div className="flex items-center gap-2">
        <Typography.Text type="secondary">
          Phase permutation mode:
        </Typography.Text>
        <Tag
          color={
            configuration.phase_permutation_mode === "use_topology"
              ? "blue"
              : "default"
          }
        >
          {configuration.phase_permutation_mode ?? "worst_case"}
        </Tag>
      </div>
      {configuration.phase_permutation_mode === "use_topology" &&
        undeclaredRotations.length > 0 && (
          <PhaseRotationWarning undeclaredRotations={undeclaredRotations} />
        )}
      {assetEntries.length === 0 ? (
        <Empty description="No asset IO configuration entries" />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {assetEntries.map((entry) => (
            <AssetCard key={entry.asset_id} entry={entry} />
          ))}
        </div>
      )}
    </div>
  );
};
